import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart3, User, CheckCircle2, Circle, Flame } from "lucide-react";
import { Link } from "react-router-dom";

const PRIORITY_META = {
  visok: { color: "#C65D47", label: "Visok" },
  srednji: { color: "#D99A5B", label: "Srednji" },
  nizak: { color: "#82937E", label: "Nizak" },
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/analytics/summary")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Nemaš pristup analitici"));
  }, []);

  if (err) {
    return (
      <div className="surface-card p-8 text-center">
        <BarChart3 className="w-10 h-10 mx-auto text-ink-muted" />
        <div className="mt-3 font-heading text-lg">Analitika je zaključana</div>
        <div className="text-sm text-ink-muted mt-1">{err}</div>
      </div>
    );
  }
  if (!data) return <div className="text-ink-muted">Učitavanje...</div>;

  const totalPri = Object.values(data.by_priority || {}).reduce((s, v) => s + v, 0) || 1;
  const maxOpen = Math.max(1, ...data.by_collaborator.map((c) => c.total));

  return (
    <div className="space-y-8">
      <div>
        <div className="overline">Admin</div>
        <h1 className="mt-1 text-3xl md:text-4xl font-heading font-semibold tracking-tight">Analitika</h1>
        <p className="text-ink-muted mt-2">Ko koliko ima otvorenih i završenih zadataka.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat testid="an-open" label="Otvoreni ukupno" value={data.totals.open} color="#2C402E" />
        <Stat testid="an-done" label="Završeni ukupno" value={data.totals.done} color="#82937E" />
        <Stat testid="an-collabs" label="Aktivni saradnici" value={data.totals.collaborators} color="#D99A5B" />
      </div>

      <div className="surface-card p-6">
        <div className="overline flex items-center gap-1.5"><Flame className="w-3 h-3" /> Distribucija po prioritetu (otvoreni)</div>
        <div className="mt-4 h-3 rounded-full overflow-hidden bg-surface-secondary flex">
          {["visok", "srednji", "nizak"].map((p) => {
            const val = data.by_priority?.[p] || 0;
            const pct = (val / totalPri) * 100;
            return <div key={p} title={`${PRIORITY_META[p].label}: ${val}`} style={{ width: `${pct}%`, background: PRIORITY_META[p].color }} />;
          })}
        </div>
        <div className="mt-3 flex gap-4 text-xs">
          {["visok", "srednji", "nizak"].map((p) => (
            <div key={p} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded" style={{ background: PRIORITY_META[p].color }} />
              {PRIORITY_META[p].label}: <b>{data.by_priority?.[p] || 0}</b>
            </div>
          ))}
        </div>
      </div>

      <div className="surface-card">
        <div className="p-5 border-b border-line">
          <div className="font-heading font-semibold">Po saradniku</div>
          <div className="text-xs text-ink-muted">Sortirano po otvorenim zadacima.</div>
        </div>
        {data.by_collaborator.length === 0 ? (
          <div className="p-8 text-center text-ink-muted">Nema podataka.</div>
        ) : (
          data.by_collaborator.map((c) => (
            <div key={c.collaborator_id} className="p-4 border-b border-line last:border-b-0" data-testid={`an-collab-${c.collaborator_id}`}>
              <div className="flex items-center gap-3">
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt={c.name} className="w-9 h-9 rounded-md object-cover border border-line" />
                ) : (
                  <div className="w-9 h-9 rounded-md bg-surface-secondary border border-line grid place-items-center text-ink-muted">
                    <User className="w-4 h-4" />
                  </div>
                )}
                <Link to={`/saradnici/${c.collaborator_id}`} className="font-medium hover:text-forest flex-1 min-w-0 truncate">{c.name}</Link>
                <div className="text-sm text-ink-muted flex items-center gap-4">
                  <span className="inline-flex items-center gap-1"><Circle className="w-3 h-3 text-forest" /> {c.open}</span>
                  <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-sage" /> {c.done}</span>
                </div>
              </div>
              <div className="mt-2 h-2 bg-surface-secondary rounded-full overflow-hidden flex">
                <div style={{ width: `${(c.open / maxOpen) * 100}%`, background: "#2C402E" }} />
                <div style={{ width: `${(c.done / maxOpen) * 100}%`, background: "#82937E" }} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div data-testid={testid} className="surface-card p-5">
      <div className="overline">{label}</div>
      <div className="mt-2 text-3xl font-heading font-semibold" style={{ color }}>{value}</div>
    </div>
  );
}
